import React, { useState, useEffect } from 'react';
import { Edit16Regular, Delete16Regular } from '@fluentui/react-icons';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/Dialog';
import { SketchPicker } from 'react-color';
import { toast } from 'sonner';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import PatternPreview from './PatternPreview';
import PatternSearchDialog from './PatternSearchDialog';
import CombinedPatternPreview from './CombinedPatternPreview';
import { useUpdateMaterialPattern, useMaterialPattern } from '../../../hooks/api';
import {
  PatternSettings,
  MaterialParametersDialogProps,
  UploadedPattern,
  isSolidPattern,
  SOLID_PATTERN,
} from '../../../types';

const MaterialParametersDialog = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  materialPatterns,
  solutionId,
}: MaterialParametersDialogProps) => {
  const [foreground, setForeground] = useState<PatternSettings>(
    initialValues?.foreground || { pattern: null, color: '#000000', scale: 1 }
  );
  const [background, setBackground] = useState<PatternSettings>(
    initialValues?.background || { pattern: null, color: '#FFFFFF', scale: 1 }
  );
  const [foregroundMaxScale, setForegroundMaxScale] = useState(5);
  const [backgroundMaxScale, setBackgroundMaxScale] = useState(5);
  const [colorPickerOpen, setColorPickerOpen] = useState<'foreground' | 'background' | null>(null);
  const [patternSearchOpen, setPatternSearchOpen] = useState<'foreground' | 'background' | null>(
    null
  );

  const { mutate: updateMaterialPattern, isLoading: isSaving } = useUpdateMaterialPattern();

  const { foregroundPattern, backgroundPattern, loadingPatterns } =
    useMaterialPattern(materialPatterns);

  useEffect(() => {
    if (open && materialPatterns && !initialValues && !loadingPatterns) {
      const fgPatternId = materialPatterns.fg_pattern?.id;
      const bgPatternId = materialPatterns.bg_pattern?.id;

      const fgPattern: UploadedPattern | null =
        fgPatternId && foregroundPattern
          ? {
              id: fgPatternId,
              name: materialPatterns.fg_pattern.title,
              pattern: foregroundPattern,
              fileContent: '',
            }
          : !fgPatternId
          ? SOLID_PATTERN
          : null;

      const bgPattern: UploadedPattern | null =
        bgPatternId && backgroundPattern
          ? {
              id: bgPatternId,
              name: materialPatterns.bg_pattern.title,
              pattern: backgroundPattern,
              fileContent: '',
            }
          : !bgPatternId
          ? SOLID_PATTERN
          : null;

      setForeground({
        pattern: fgPattern,
        color: materialPatterns.fg_color_hex || '#000000',
        scale: materialPatterns.fg_scale ?? 1,
      });

      setBackground({
        pattern: bgPattern,
        color: materialPatterns.bg_color_hex || '#FFFFFF',
        scale: materialPatterns.bg_scale ?? 1,
      });
    } else if (initialValues) {
      setForeground(initialValues.foreground);
      setBackground(initialValues.background);
    }
  }, [
    open,
    materialPatterns,
    foregroundPattern,
    backgroundPattern,
    loadingPatterns,
    initialValues,
  ]);

  useEffect(() => {
    if (!open) {
      setColorPickerOpen(null);
      setPatternSearchOpen(null);
      if (!initialValues) {
        setForeground({ pattern: null, color: '#000000', scale: 1 });
        setBackground({ pattern: null, color: '#FFFFFF', scale: 1 });
      }
    }
  }, [open, initialValues]);

  const handleConfirm = () => {
    if (!solutionId) {
      // New solution - save locally, will be persisted after solution creation
      onSubmit?.({ foreground, background });
      onClose();
      return;
    }

    const fgIsSolid = isSolidPattern(foreground.pattern);
    const bgIsSolid = isSolidPattern(background.pattern);

    updateMaterialPattern(
      {
        solutionId,
        data: {
          fg_pattern: fgIsSolid ? null : foreground.pattern?.id || null,
          bg_pattern: bgIsSolid ? null : background.pattern?.id || null,
          fg_color_hex: foreground.color,
          bg_color_hex: background.color,
          fg_pattern_detail: { title: fgIsSolid ? 'Solid' : foreground.pattern?.name || '' },
          bg_pattern_detail: { title: bgIsSolid ? 'Solid' : background.pattern?.name || '' },
          fg_scale: fgIsSolid ? null : foreground.scale,
          bg_scale: bgIsSolid ? null : background.scale,
        },
      },
      {
        onSuccess: () => {
          toast.success('Vzor materiálu uložen');
          onSubmit?.({ foreground, background });
          onClose();
        },
        onError: (error) => {
          console.error(error);
        },
      }
    );
  };

  const handlePatternSelect = (pattern: UploadedPattern, type: 'foreground' | 'background') => {
    if (type === 'foreground') {
      setForeground((prev) => ({ ...prev, pattern }));
    } else {
      setBackground((prev) => ({ ...prev, pattern }));
    }
    setPatternSearchOpen(null);
  };

  const handleColorChange = (type: 'foreground' | 'background', color: { hex: string }) => {
    if (type === 'foreground') {
      setForeground((prev) => ({ ...prev, color: color.hex }));
    } else {
      setBackground((prev) => ({ ...prev, color: color.hex }));
    }
  };

  const handleDeletePattern = (type: 'foreground' | 'background') => {
    if (type === 'foreground') {
      setForeground((prev) => ({ ...prev, pattern: null }));
    } else {
      setBackground((prev) => ({ ...prev, pattern: null }));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Definovani vzoru pro řez</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {loadingPatterns ? (
              <div className="flex items-center justify-center py-8">
                <SpinnerOverlay size="lg" tip="Načítání vzorů..." />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Combined Preview */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Náhled</label>
                  <div className="relative w-full h-64 border border-neutral-300 rounded overflow-hidden bg-color4">
                    <CombinedPatternPreview
                      foreground={foreground.pattern?.pattern}
                      foregroundColor={foreground.color}
                      foregroundScale={foreground.scale}
                      background={background.pattern?.pattern}
                      backgroundColor={background.color}
                      backgroundScale={background.scale}
                    />
                  </div>
                </div>

                {/* Foreground */}
                <div className="space-y-4 border-b border-neutral-200 pb-4">
                  <label className="text-sm font-medium">Popředí</label>

                  {/* Pattern */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Vzor v řezu, popředí</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1 h-20 border border-neutral-300 rounded overflow-hidden bg-color4">
                        {foreground.pattern ? (
                          <PatternPreview
                            pattern={foreground.pattern.pattern}
                            color={foreground.color}
                            patternScale={foreground.scale}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <span className="text-sm text-neutral-500">Vyberte vzor</span>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPatternSearchOpen('foreground')}
                        data-testid="material-params-fg-pattern-edit-btn"
                      >
                        <Edit16Regular />
                      </Button>
                      {foreground.pattern && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleDeletePattern('foreground')}
                          data-testid="material-params-fg-pattern-delete-btn"
                        >
                          <Delete16Regular />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Color */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Barva v řezu, popředí</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1 p-2 border border-neutral-300 rounded bg-white">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded border border-neutral-300 cursor-pointer"
                            style={{ backgroundColor: foreground.color }}
                            onClick={() =>
                              setColorPickerOpen(
                                colorPickerOpen === 'foreground' ? null : 'foreground'
                              )
                            }
                            data-testid="material-params-fg-color-swatch"
                          />
                          <span className="text-sm">{foreground.color}</span>
                        </div>
                        {colorPickerOpen === 'foreground' && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setColorPickerOpen(null)}
                            />
                            <div className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                              <SketchPicker
                                color={foreground.color}
                                onChange={(color) => handleColorChange('foreground', color)}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Scale - hidden for solid pattern */}
                  {!isSolidPattern(foreground.pattern) && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Měřítko vzoru, popředí</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0.1"
                          max={foregroundMaxScale}
                          step="0.1"
                          value={foreground.scale}
                          onChange={(e) =>
                            setForeground((prev) => ({
                              ...prev,
                              scale: parseFloat(e.target.value),
                            }))
                          }
                          className="flex-1 h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          data-testid="material-params-fg-scale-range"
                        />
                        <span className="text-sm font-mono w-12 text-right">
                          {foreground.scale.toFixed(1)}×
                        </span>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={foregroundMaxScale}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            if (val >= 1) {
                              setForegroundMaxScale(val);
                              if (foreground.scale > val) {
                                setForeground((prev) => ({ ...prev, scale: val }));
                              }
                            }
                          }}
                          className="w-16 px-1 py-0.5 text-sm border border-neutral-300 rounded text-center"
                          title="Max měřítko"
                          data-testid="material-params-fg-max-scale-input"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Background */}
                <div className="space-y-4">
                  <label className="text-sm font-medium">Pozadí</label>

                  {/* Pattern */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Vzor v řezu, pozadí</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1 h-20 border border-neutral-300 rounded overflow-hidden bg-color4">
                        {background.pattern ? (
                          <PatternPreview
                            pattern={background.pattern.pattern}
                            color={background.color}
                            patternScale={background.scale}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <span className="text-sm text-neutral-500">Vyberte vzor</span>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPatternSearchOpen('background')}
                        data-testid="material-params-bg-pattern-edit-btn"
                      >
                        <Edit16Regular />
                      </Button>
                      {background.pattern && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleDeletePattern('background')}
                          data-testid="material-params-bg-pattern-delete-btn"
                        >
                          <Delete16Regular />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Color */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Barva v řezu, pozadí</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1 p-2 border border-neutral-300 rounded bg-white">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded border border-neutral-300 cursor-pointer"
                            style={{ backgroundColor: background.color }}
                            onClick={() =>
                              setColorPickerOpen(
                                colorPickerOpen === 'background' ? null : 'background'
                              )
                            }
                            data-testid="material-params-bg-color-swatch"
                          />
                          <span className="text-sm">{background.color}</span>
                        </div>
                        {colorPickerOpen === 'background' && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setColorPickerOpen(null)}
                            />
                            <div className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                              <SketchPicker
                                color={background.color}
                                onChange={(color) => handleColorChange('background', color)}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Scale - hidden for solid pattern */}
                  {!isSolidPattern(background.pattern) && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Měřítko vzoru, pozadí</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0.1"
                          max={backgroundMaxScale}
                          step="0.1"
                          value={background.scale}
                          onChange={(e) =>
                            setBackground((prev) => ({
                              ...prev,
                              scale: parseFloat(e.target.value),
                            }))
                          }
                          className="flex-1 h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          data-testid="material-params-bg-scale-range"
                        />
                        <span className="text-sm font-mono w-12 text-right">
                          {background.scale.toFixed(1)}×
                        </span>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={backgroundMaxScale}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            if (val >= 1) {
                              setBackgroundMaxScale(val);
                              if (background.scale > val) {
                                setBackground((prev) => ({ ...prev, scale: val }));
                              }
                            }
                          }}
                          className="w-16 px-1 py-0.5 text-sm border border-neutral-300 rounded text-center"
                          title="Max měřítko"
                          data-testid="material-params-bg-max-scale-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                variant="outline"
                disabled={isSaving}
                data-testid="material-params-cancel-btn"
              >
                Zrušit
              </Button>
            </DialogClose>
            <Button
              onClick={handleConfirm}
              disabled={isSaving}
              data-testid="material-params-confirm-btn"
            >
              {isSaving ? (
                <>
                  <SpinnerOverlay size="sm" />
                  <span>Ukládání...</span>
                </>
              ) : (
                'Potvrdit'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pattern Search Dialog */}
      {patternSearchOpen && (
        <PatternSearchDialog
          open={true}
          onClose={() => setPatternSearchOpen(null)}
          onSelect={(pattern) => {
            if (patternSearchOpen) {
              handlePatternSelect(pattern, patternSearchOpen);
            }
          }}
        />
      )}
    </>
  );
};

export default MaterialParametersDialog;
